/**
 * AudioPlayer Component
 *
 * Diamond Architecture: Client component for audio playback.
 * A styled audio player for blog post articles with custom controls.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import {
  plaintextString,
  type LocalizedText,
  type PlaintextString,
} from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { useJsOnlyInert } from '@letsgethome/ui/hooks/use-hydrated';
import { cn } from '@letsgethome/ui/lib/utils';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface AudioPlayerProps {
  src: string;
  title?: LocalizedText;
  className?: string;
}

export function AudioPlayer({ src, title, className }: AudioPlayerProps) {
  // The seek bar is a real control (role=slider, keyboard-driven) whose whole
  // behaviour arrives with React; `inert` is the <div> analogue of `disabled`.
  const seekInert = useJsOnlyInert();
  const tA11y = useTranslations('common');
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Don't block on metadata loading - user can click play immediately
  // isBuffering shows spinner only while actively buffering during playback
  const [isBuffering, setIsBuffering] = useState(false);

  // Update current time as audio plays
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handleWaiting = () => setIsBuffering(true);
    const handleCanPlay = () => setIsBuffering(false);
    const handlePlaying = () => setIsBuffering(false);
    const handleError = () => {
      setIsBuffering(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('error', handleError);

    // Check if duration already available
    if (audio.duration && isFinite(audio.duration)) {
      setDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.muted = !audio.muted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      audio.currentTime = percentage * duration;
    },
    [duration]
  );

  const formatTime = (time: number): PlaintextString => {
    if (!isFinite(time)) return plaintextString('0:00');
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return plaintextString(`${minutes}:${seconds.toString().padStart(2, '0')}`);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={cn(
        'flex items-center gap-4 p-4 bg-muted/50 rounded-xl border border-border',
        className
      )}
    >
      {/* Hidden audio element */}
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Play/Pause Button */}
      <Button
        type="button"
        onClick={togglePlay}
        disabled={isBuffering}
        size="icon"
        className={cn(
          'flex-shrink-0 h-12 w-12 rounded-full',
          'transition-all duration-200 hover:scale-105',
          'disabled:cursor-not-allowed disabled:hover:scale-100'
        )}
        aria-label={isPlaying ? tA11y('a11y.pause') : tA11y('a11y.play')}
      >
        {isBuffering ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-5 h-5" />
        ) : (
          <Play className="w-5 h-5 ml-0.5" />
        )}
      </Button>

      {/* Progress and Info */}
      <div className="flex-1 min-w-0">
        {/* Title */}
        {/* eslint-disable-next-line i18next/no-literal-string -- decorative emoji prefix */}
        <p className="text-sm font-medium text-foreground truncate mb-1">🎧 {title}</p>

        {/* Progress Bar */}
        <div
          role="slider"
          inert={seekInert}
          tabIndex={0}
          aria-label={tA11y('a11y.seek')}
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
          onClick={handleSeek}
          onKeyDown={(e) => {
            const audio = audioRef.current;
            if (!audio) return;
            if (e.key === 'ArrowRight') audio.currentTime = Math.min(duration, currentTime + 5);
            if (e.key === 'ArrowLeft') audio.currentTime = Math.max(0, currentTime - 5);
          }}
          className="group relative h-2 bg-muted-foreground/20 rounded-full cursor-pointer overflow-hidden"
        >
          {/* Progress fill */}
          <div
            className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
          {/* Hover indicator */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>

        {/* Time */}
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Mute Button */}
      <UnstyledButton
        type="button"
        onClick={toggleMute}
        className={cn(
          'flex-shrink-0 p-2 rounded-full',
          'text-muted-foreground hover:text-foreground hover:bg-muted',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-primary'
        )}
        aria-label={isMuted ? tA11y('a11y.unmute') : tA11y('a11y.mute')}
      >
        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
      </UnstyledButton>
    </div>
  );
}
